import { Field, Query } from '@tilework/opus';

import {
    companyAdminInfo,
    companyUser, companyUserInfo, companyUsersOptions, companyUsersOutput, userExtensionAttributes, usersPageInfo,
} from 'Query/MyAccountCompany.type';

/** @namespace Steinkrueger/Query/MyAccountCompany/Query */
export class MyAccountCompanyQuery {
    options: companyUsersOptions = {
        pageSize: 20,
        currentPage: 1,
    };

    getQuery(): Query<'awCaGetCompanyForCurrentCustomer', string> {
        return new Query<'awCaGetCompanyForCurrentCustomer', any>('awCaGetCompanyForCurrentCustomer')
            .addFieldList(this._getMyAccountCompanyFields());
    }

    getUsers(options: companyUsersOptions): Query<'awCaCompanyUserList', companyUsersOutput> {
        if (!options) {
            throw new Error('Missing argument `options`');
        }

        this.options = options;

        return this._getUsersList();
    }

    _getUsersList(): Query<'awCaCompanyUserList', companyUsersOutput> {
        return new Query<'awCaCompanyUserList', companyUsersOutput>('awCaCompanyUserList')
            .addFieldList(this._getUsersFields());
    }

    _getUsersFields(): Array<
    Field<'items', companyUser, true>
    | Field<'total_count', number>
    | Field<'page_info', usersPageInfo>
    > {
        return [
            this._getItemsField(),
            new Field<'total_count', number>('total_count'),
            this._getPageInfoField(),
        ];
    }

    _getItemsField(): Field<'items', companyUser, true> {
        return new Field<'items', companyUser, true>('items', true)
            .addFieldList(this._getUserInterfaceFields());
    }

    _getPageInfoField(): Field<'page_info', usersPageInfo> {
        return new Field<'page_info', usersPageInfo>('page_info')
            .addField(new Field<'current_page', number>('current_page'))
            .addField(new Field<'page_size', number>('page_size'));
    }

    _getUserExtensionAttributes(): Field<'extension_attributes', userExtensionAttributes> {
        return new Field<'extension_attributes', userExtensionAttributes>('extension_attributes')
            .addField(this._getCompanyUserInfo());
    }

    _getCompanyUserInfo(): Field<'aw_ca_company_user', companyUserInfo> {
        return new Field<'aw_ca_company_user', companyUserInfo>('aw_ca_company_user')
            .addFieldList(this._getCompanyUserFields());
    }

    _getCompanyAdminInfo(): Field<'admin_info', companyAdminInfo> {
        return new Field<'admin_info', companyAdminInfo>('admin_info')
            .addFieldList(this._getCompanyAdminFields());
    }

    _getCompanyAdminFields(): Array<
    Field<'firstname', string>
    | Field<'lastname', string>
    | Field<'email', string>
    | Field<'job_title', string>
    | Field<'telephone', string>
    > {
        return [
            new Field<'firstname', string>('firstname'),
            new Field<'lastname', string>('lastname'),
            new Field<'email', string>('email'),
            new Field<'job_title', string>('job_title'),
            new Field<'telephone', string>('telephone'),
        ];
    }

    _getCompanyUserFields(): Array<
    Field<'job_title', string>
    | Field<'status', string>
    | Field<'telephone', string>
    > {
        return [
            new Field<'job_title', string>('job_title'),
            new Field<'status', string>('status'),
            new Field<'telephone', string>('telephone'),
        ];
    }

    _getUserInterfaceFields(): Array<
    Field<'email', string>
    | Field<'firstname', string>
    | Field<'lastname', string>
    | Field<'id', number>
    | Field<'extension_attributes', userExtensionAttributes>
    > {
        return [
            new Field<'email', string>('email'),
            new Field<'firstname', string>('firstname'),
            new Field<'lastname', string>('lastname'),
            new Field<'id', number>('id'),
            this._getUserExtensionAttributes(),
        ];
    }

    _getMyAccountCompanyFields(): Array<
    Field<'name', string, true>
    | Field<'email', string, true>
    | Field<'street', string, true>
    | Field<'city', string, true>
    | Field<'region', string, true>
    | Field<'postcode', string, true>
    | Field<'country_id', string, true>
    | Field<'telephone', string, true>
    | Field<'sales_representative_id', number, true>
    | Field<'admin_info', companyAdminInfo>
    > {
        return [
            new Field<'name', string, true>('name'),
            new Field<'email', string, true>('email'),
            new Field<'street', string, true>('street'),
            new Field<'city', string, true>('city'),
            new Field<'region', string, true>('region'),
            new Field<'postcode', string, true>('postcode'),
            new Field<'country_id', string, true>('country_id'),
            new Field<'telephone', string, true>('telephone'),
            new Field<'sales_representative_id', number, true>('sales_representative_id'),
            this._getCompanyAdminInfo(),
        ];
    }
}

export default new MyAccountCompanyQuery();
